import { StateCreator } from "zustand";
import { StreamingState } from "./StreamingStore";
import { TArtifact } from "src/views/assistant/types";
import { deriveCompletionMessage } from "src/reusable/Chat/AssistantMessage/artifacts/toolStatusUtils";
import { extractToolCallId } from "src/reusable/Chat/ChatStreaming/services/contentTypeUtils";

export type ToolCallInfo = {
  artifactId: string;
  messageId: string | null;
  parentMessageId: string | null;
  toolName: string;
};

export interface ArtifactSlice {
  artifacts: Record<string, TArtifact>;
  toolCallInfoQueue: ToolCallInfo[];

  addArtifact: (artifact: TArtifact) => void;
  updateArtifactLoading: (id: string, isLoading: boolean) => void;
  updateArtifactContent: (
    id: string,
    content: any,
    statusMessage?: string,
  ) => void;
  updateArtifactCrSearchResults: (id: string, crSearchResults: any[]) => void;
  getArtifactsByMessageId: (messageId: string) => TArtifact[];
  /** Find a loading artifact by messageId and toolName for deterministic response matching */
  findLoadingArtifactByToolName: (
    messageId: string,
    toolName: string,
  ) => TArtifact | undefined;
  hasArtifact: (id: string) => boolean;
  removeArtifact: (id: string) => void;
  clearArtifacts: () => void;
  clearArtifactsByMessageId: (messageId: string) => void;
  appendToolCallInfoQueue: (info: ToolCallInfo) => void;
  popToolCallInfoQueue: () => ToolCallInfo | undefined;
  getToolCallInfoQueueLength: () => number;
  setToolCallLoading: (artifactId: string, isLoading: boolean) => void;
  resetToolCallLoadingStates: () => void;
  resetArtifactsStore: () => void;
  reconstructArtifactsFromMessages: (messages: any[]) => void;
}

export const createArtifactSlice: StateCreator<
  StreamingState,
  [["zustand/subscribeWithSelector", never]],
  [],
  ArtifactSlice
> = (set, get) => ({
  artifacts: {},
  toolCallInfoQueue: [],

  addArtifact: (artifact) =>
    set((state) => ({
      artifacts: {
        ...state.artifacts,
        [artifact.id]: artifact,
      },
    })),

  updateArtifactLoading: (id, isLoading) =>
    set((state) => {
      const artifact = state.artifacts[id];
      if (!artifact) return state;

      return {
        artifacts: {
          ...state.artifacts,
          [id]: {
            ...artifact,
            isLoading,
          },
        },
      };
    }),

  updateArtifactContent: (id, content, statusMessage?) => {
    set((state) => {
      const existing = state.artifacts[id];
      if (!existing) return state;
      const updatedArtifacts = {
        ...state.artifacts,
        [id]: {
          ...existing,
          content: { text: content },
          isLoading: false,
          ...(statusMessage !== undefined && { statusMessage }),
        },
      };
      return { artifacts: updatedArtifacts };
    });
  },

  updateArtifactCrSearchResults: (id, crSearchResults) =>
    set((state) => {
      const artifact = state.artifacts[id];
      if (!artifact) return state;

      return {
        artifacts: {
          ...state.artifacts,
          [id]: {
            ...artifact,
            crSearchResults,
          },
        },
      };
    }),

  getArtifactsByMessageId: (messageId) => {
    const state = get();
    return Object.values(state.artifacts).filter(
      (artifact) => artifact.messageId === messageId,
    );
  },

  findLoadingArtifactByToolName: (messageId, toolName) => {
    const state = get();
    // Find artifacts for this message with matching toolName that are still loading
    // Sort by artifact ID (which includes partIndex) to ensure deterministic order
    const matchingArtifacts = Object.values(state.artifacts)
      .filter(
        (artifact) =>
          artifact.messageId === messageId &&
          artifact.toolName === toolName &&
          (artifact.isLoading || artifact.content?.text === null),
      )
      .sort((a, b) => a.id.localeCompare(b.id));

    // Return the first loading artifact with this tool name
    return matchingArtifacts[0];
  },

  hasArtifact: (id: string) => {
    return !!get().artifacts[id];
  },

  removeArtifact: (id) =>
    set((state) => {
      const { [id]: removed, ...remaining } = state.artifacts;
      return { artifacts: remaining };
    }),

  clearArtifacts: () =>
    set(() => ({
      artifacts: {},
      toolCallInfoQueue: [],
    })),

  clearArtifactsByMessageId: (messageId) =>
    set((state) => {
      const newArtifacts = { ...state.artifacts };
      const artifactIds = new Set<string>();

      Object.keys(newArtifacts).forEach((key) => {
        if (newArtifacts[key].messageId === messageId) {
          artifactIds.add(key);
          delete newArtifacts[key];
        }
      });

      const newQueue = state.toolCallInfoQueue.filter(
        (info) => !artifactIds.has(info.artifactId),
      );

      return {
        artifacts: newArtifacts,
        toolCallInfoQueue: newQueue,
      };
    }),

  appendToolCallInfoQueue: (info) =>
    set((state) => ({
      toolCallInfoQueue: [...state.toolCallInfoQueue, info],
    })),

  popToolCallInfoQueue: () => {
    const state = get();
    if (state.toolCallInfoQueue.length === 0) {
      return undefined;
    }

    const [first, ...rest] = state.toolCallInfoQueue;
    set({ toolCallInfoQueue: rest });
    return first;
  },

  getToolCallInfoQueueLength: () => {
    return get().toolCallInfoQueue.length;
  },

  setToolCallLoading: (artifactId, isLoading) =>
    set((state) => {
      const artifact = state.artifacts[artifactId];
      if (!artifact) return state;

      return {
        artifacts: {
          ...state.artifacts,
          [artifactId]: {
            ...artifact,
            isLoading,
          },
        },
      };
    }),

  resetToolCallLoadingStates: () =>
    set((state) => {
      const pendingArtifactIds = state.toolCallInfoQueue.map(
        (item) => item.artifactId,
      );

      const updatedArtifacts = { ...state.artifacts };

      pendingArtifactIds.forEach((id) => {
        if (updatedArtifacts[id]) {
          updatedArtifacts[id] = {
            ...updatedArtifacts[id],
            isLoading: false,
          };
        }
      });

      return {
        artifacts: updatedArtifacts,
        toolCallInfoQueue: [],
      };
    }),
  resetArtifactsStore: () =>
    set(() => ({
      artifacts: {},
      toolCallInfoQueue: [],
    })),

  // Reconstruct artifacts from historical dialogue messages.
  // Artifact IDs use the tool_response's partIndex to match the IDs
  // created by the ToolResponse component during rendering, preventing
  // duplicates when both paths run on page reload.
  reconstructArtifactsFromMessages: (messages: any[]) =>
    set((state) => {
      const newArtifacts: Record<string, TArtifact> = {};

      messages.forEach((message) => {
        if (!message.contentParts || !message.id) return;

        // Queue parsed tool call info to match with responses in order (FIFO)
        const toolCallQueue: {
          toolCall: any;
          toolCallPartIndex: number;
        }[] = [];

        message.contentParts.forEach((part: any, partIndex: number) => {
          const ContentType = {
            TEXT: "text",
            THINK: "think",
            TOOL_CALL: "tool_call",
            TOOL_RESPONSE: "tool_response",
          };

          if (part.type === ContentType.TOOL_CALL) {
            // Strip tool_update tags before parsing JSON
            let cleanedText = part.text;
            cleanedText = cleanedText.replace(
              /<tool_update>[\s\S]*?<\/tool_update>/g,
              "",
            );
            const rawToolCall = cleanedText
              .replace(/^<tool_call>\s*/, "")
              .replace(/\s*<\/tool_call>$/, "");

            try {
              let toolCall;
              try {
                toolCall = JSON.parse(rawToolCall);
              } catch {
                // Early + final JSON may be concatenated; parse the last object
                const boundary = rawToolCall.lastIndexOf("}{");
                if (boundary > 0) {
                  toolCall = JSON.parse(rawToolCall.slice(boundary + 1));
                } else {
                  throw new Error("Unparseable tool call JSON");
                }
              }

              // Defer artifact creation until we find the matching tool_response.
              toolCallQueue.push({ toolCall, toolCallPartIndex: partIndex });
            } catch (e) {
              console.error(
                `Failed to parse tool call in message ${message.id}:`,
                e,
              );
            }
          } else if (
            part.type === ContentType.TOOL_RESPONSE &&
            toolCallQueue.length > 0
          ) {
            // Match by tool_call_id when both sides carry one — parallel
            // tool calls make positional FIFO unreliable. FIFO remains the
            // fallback for legacy parts without ids.
            const responseId = extractToolCallId(part.text);
            let matchIdx = responseId
              ? toolCallQueue.findIndex(
                  (q) => q.toolCall?.tool_call_id === responseId,
                )
              : -1;
            if (matchIdx === -1) matchIdx = 0;
            const matchingInfo = toolCallQueue.splice(matchIdx, 1)[0]!;

            // Use the tool_response's partIndex for the artifact ID.
            const artifactId = `artifact-${message.id}-${matchingInfo.toolCall.name}-${partIndex}`;

            const rawResponse = part.text
              .replace(/^<tool_response>\s*/, "")
              .replace(/\s*<\/tool_response>$/, "");

            try {
              const responseContent = JSON.parse(rawResponse);
              const completionMsg = deriveCompletionMessage(responseContent);

              newArtifacts[artifactId] = {
                id: artifactId,
                messageId: message.id,
                parentId: message.parent_id || "",
                toolName: matchingInfo.toolCall.name,
                type: matchingInfo.toolCall.name,
                isLoading: false,
                content: { text: responseContent },
                ...(completionMsg && { statusMessage: completionMsg }),
              };
            } catch (e) {
              // Fallback to raw text if not valid JSON
              newArtifacts[artifactId] = {
                id: artifactId,
                messageId: message.id,
                parentId: message.parent_id || "",
                toolName: matchingInfo.toolCall.name,
                type: matchingInfo.toolCall.name,
                isLoading: false,
                content: { text: rawResponse },
              };
            }
          }
        });

        // Create artifacts for any unmatched tool calls (no response received,
        // e.g. streaming was interrupted). Use the tool_call's own partIndex.
        toolCallQueue.forEach(({ toolCall, toolCallPartIndex }) => {
          const artifactId = `artifact-${message.id}-${toolCall.name}-${toolCallPartIndex}`;
          newArtifacts[artifactId] = {
            id: artifactId,
            messageId: message.id,
            parentId: message.parent_id || "",
            toolName: toolCall.name,
            type: toolCall.name,
            isLoading: true,
            content: { text: null },
            statusMessage: toolCall.status_message || undefined,
          };
        });
      });

      return {
        artifacts: { ...state.artifacts, ...newArtifacts },
      };
    }),
});
